/**
 * Does a recipe have anything in it to cook or shop for?
 *
 * Seven saved recipes had no ingredient at all (#745 follow-up): drafts from
 * "Ajouter manuellement" that were closed before anything was typed, and
 * imports that failed to read the page. The planners proposed them like any
 * other dinner — a slot you cannot cook, and nothing on the grocery list.
 *
 * Structural on purpose: a Firestore document, a `Recipe` and an index entry
 * all pass. An index entry carries ingredient names as `ingredients: string[]`,
 * a document carries `extendedIngredients` and/or the legacy `ingredients`.
 */
export interface ContentCheckable {
  extendedIngredients?: readonly unknown[] | null;
  ingredients?: readonly unknown[] | null;
  instructions?: readonly unknown[] | null;
}

const lineText = (line: unknown): string => {
  if (typeof line === 'string') return line;
  if (line && typeof line === 'object') {
    const o = line as Record<string, unknown>;
    const text = o.original ?? o.name ?? o.step;
    return typeof text === 'string' ? text : '';
  }
  return '';
};

/** A line counts only if it has text — an array of blanks is empty. */
const countLines = (lines: readonly unknown[] | null | undefined): number =>
  Array.isArray(lines) ? lines.filter((l) => lineText(l).trim() !== '').length : 0;

export function ingredientCount(recipe: ContentCheckable): number {
  return Math.max(countLines(recipe.extendedIngredients), countLines(recipe.ingredients));
}

/** The planning gate: a recipe with no ingredient is never put in a slot. */
export function hasIngredients(recipe: ContentCheckable): boolean {
  return ingredientCount(recipe) > 0;
}

/** Nothing typed at all: no ingredient and no step. What an abandoned draft looks like. */
export function isBlankRecipe(recipe: ContentCheckable): boolean {
  return !hasIngredients(recipe) && countLines(recipe.instructions) === 0;
}

/**
 * The ingredient lines an index entry carries. The index copies the document's
 * legacy `ingredients` strings; a document that only has `extendedIngredients`
 * would otherwise index as empty and be kept out of every plan.
 */
export function indexIngredientLines(data: { ingredients?: unknown; extendedIngredients?: unknown }): string[] {
  const legacy = Array.isArray(data.ingredients)
    ? data.ingredients.filter((l): l is string => typeof l === 'string' && l.trim() !== '')
    : [];
  if (legacy.length > 0) return legacy;
  return Array.isArray(data.extendedIngredients)
    ? data.extendedIngredients.map(lineText).filter((l) => l.trim() !== '')
    : [];
}
